import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import { Launchpool } from '@flipster/apis/v1/launchpool/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import Formatted from '@flipster/react/design-utils/formatted';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { StepModel } from '@flipster/react/misc/step/use-steps';
import { css } from '@flipster/styled-system/css';
import { getFlipsterConfigsV1Key, getLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { Steps } from '..';
import { StakingFormSchema } from '../../validation';
import StakingCurrencyTradabilityInfo from '../staking-currency-tradability-info';

interface Props {
  pool: Launchpool;
  onClose: () => void;
  setModalStep: (step: StepModel<Steps>) => void;
}

const StakingCompletedForm = ({ pool, onClose, setModalStep }: Props) => {
  const t = useTranslations();
  const { stakingCurrency, showHedge } = pool;
  const { user } = useAuth();
  const { isMobile } = useBreakpoint();

  const { getValues } = useFormContext<StakingFormSchema>();

  const stakingAmount = getValues('amount');
  const isShowHedgeButton = showHedge && !isMobile;

  const checkSkipHedgingNotice = () => {
    if (!user?.userId) return;
    const skipHedgingNoticeKey = getFlipsterConfigsV1Key(user.userId, 'skipHedgingNoticeConfirm');
    return getLocalStorage(skipHedgingNoticeKey);
  };

  const onClickDone = () => {
    if (!checkSkipHedgingNotice() && isShowHedgeButton) {
      setModalStep({ step: 'hedgingNotice', context: undefined });
      return;
    }
    onClose();
  };

  const onClickHedging = () => {
    segment.track(StakeEvent.StakeHedgeClick, { currency: stakingCurrency, event_location: 'staking_complete_page' });
    if (!checkSkipHedgingNotice()) {
      setModalStep({ step: 'hedgingNotice', context: undefined });
      return;
    }
    setModalStep({ step: 'hedging', context: undefined });
  };

  return (
    <form>
      <Modal.Content>
        <header className={css({ textAlign: 'center', pb: 16 })}>
          <CheckCircleIcon size={40} className={css({ color: 'content.accent.info', mb: 24 })} />
          <p className={css({ textStyle: 'Title/S', color: 'content.accent.info', mb: 12 })}>
            <FormattedMessage id='Assets.r250925.Staking_completed' />
          </p>
          <Formatted.Number
            value={stakingAmount}
            suffix={stakingCurrency}
            css={{ textStyle: 'Title/L', color: 'content.default.level-5' }}
          />
        </header>
        <StakingCurrencyTradabilityInfo stakingCurrency={stakingCurrency} />
      </Modal.Content>
      <Modal.Footer css={isShowHedgeButton && { flexDir: 'column', alignItems: 'center', '& > button': { w: 328 } }}>
        <Button
          as='button'
          hierarchy='primary'
          size='large'
          onClick={onClickDone}
          label={t('Autogen_common.r251126.s_Done')}
        />
        {isShowHedgeButton && (
          <Button
            as='button'
            size='large'
            color='neutral'
            hierarchy='secondary'
            onClick={onClickHedging}
            label={t('Autogen_common.r251126.s_Go_hedge_to_protect_staked_assets')}
          />
        )}
      </Modal.Footer>
    </form>
  );
};

export default StakingCompletedForm;
